import React, { useEffect, useMemo, useState } from 'react'
import Box from '@cloudscape-design/components/box'
import Header from '@cloudscape-design/components/header'
import Spinner from '@cloudscape-design/components/spinner'
import SpaceBetween from '@cloudscape-design/components/space-between'
import { CountMetric } from '@amzn/ihm-associate-tools-js-lib'
import { getUserAlias, getUserStores } from '@api/userAndStoreContext'
import { getStoreId } from '@src/utils/EnvironmentUtil'
import { OPERATIONS_TABS } from './tabs'
import { IOperationsTabMetricsState } from './types'
import { useBundle } from '@amzn/react-arb-tools'
import { BUNDLE_NAME } from '@src/constants'
import { t } from '@src/translationHelper'
import './OperationsPage.scss'

/**
 * OperationsPage
 *
 * In-portal card-style tab page reached from the "Operations" sidebar nav item.
 *
 * The page is a generic, config-driven shell: it renders one summary card per entry in the
 * OPERATIONS_TABS registry (see tabs/). Every card follows the same structure — a title plus up
 * to two metric lines (a large primary number and a secondary status line) — and each tab supplies
 * its own metric fetcher and content panel. Adding a tab requires no changes here.
 *
 * A tab may set `isAvailable` to hide itself where its tool is not deployed (evaluated once on
 * first render); such tabs are filtered out of the registry before anything is rendered.
 *
 * Metrics are aggregated across every store the logged-in user can access (resolved via
 * getUserStores), so the cards give a network-wide summary rather than a single store's numbers.
 *
 * The metric lines use Cloudscape primitives (Box for the number/label, Spinner for loading); the
 * secondary line renders as a custom dot+text pill (.operations-metric-pill in the CSS), and the
 * card chrome (border/blue top strip/open bottom) is custom CSS, as Cloudscape has no equivalent.
 *
 * Card typography is uniform across active and inactive cards (label = heading-s, value =
 * heading-xl) so every card has the same vertical structure and the values line up in a single row
 * — only the label weight and the card chrome change with the active state.
 *
 * ToolLayout keeps this component mounted after its first visit and toggles the `hidden` prop
 * (display:none) instead of unmounting, so embedded iframes are not reloaded on every revisit.
 */

interface IOperationsPageProps {
    // When true the page is kept mounted (to preserve embedded iframes) but visually hidden.
    hidden?: boolean
}

/**
 * Renders a tab card's two metric lines from its current fetch state:
 *   line 1 — the primary value (large bold number, uniform heading-xl across all cards)
 *   line 2 — the secondary line; a colored dot+text pill when secondaryTone is set, else plain text
 */
const renderMetricLines = (state?: IOperationsTabMetricsState) => {
    // Only spin while a fetch is actively in flight. An undefined state means no fetch has run
    // (e.g. the accessible-store list is not resolved yet) — fall through to the "—" placeholder
    // instead of spinning forever; the effect re-runs and populates this once stores are known.
    if (state?.loading) {
        return <Spinner size="normal" />
    }
    const metrics = state?.metrics
    if (!metrics) {
        // No stores, fetch skipped, or fetch failed — show a neutral placeholder, not an error.
        return (
            <Box fontSize="heading-xl" fontWeight="bold" color="text-status-inactive">
                —
            </Box>
        )
    }
    return (
        <SpaceBetween size="xxs">
            {metrics.primary != null && (
                <Box fontSize="heading-xl" fontWeight="bold" color="text-label">
                    {metrics.primary}
                </Box>
            )}
            {metrics.secondary != null && (
                metrics.secondaryTone
                    ? (
                        <span className={`operations-metric-pill operations-metric-pill--${metrics.secondaryTone}`}>
                            <span className="operations-metric-pill-dot" aria-hidden="true" />
                            <span className="operations-metric-pill-text">{metrics.secondary}</span>
                        </span>
                    )
                    : <Box variant="small" color="text-body-secondary">{metrics.secondary}</Box>
            )}
        </SpaceBetween>
    )
}

const OperationsPage: React.FC<IOperationsPageProps> = ({ hidden = false }) => {
    // A tab's availability (is its tool registered here) cannot change within a session.
    // Computed first so the default tab can be derived from the filtered list.
    const tabs = useMemo(() => OPERATIONS_TABS.filter((tab) => tab.isAvailable?.() ?? true), [])
    // The default tab is the first available tab, not the first registered tab. This ensures the
    // page opens to a valid tab even if a gated tab sits at index 0 in the unfiltered registry.
    const defaultTabId = tabs[0]?.id ?? ''

    const [activeTabId, setActiveTabId] = useState<string>(defaultTabId)
    const [tabMetrics, setTabMetrics] = useState<Record<string, IOperationsTabMetricsState>>({})
    // Tabs whose content has been activated at least once — kept mounted (hidden when inactive)
    // for tabs that opt into keepMounted, so their iframes are not reloaded on tab switches.
    const [activatedTabs, setActivatedTabs] = useState<Set<string>>(
        () => new Set(defaultTabId ? [defaultTabId] : []),
    )
    // Every store the signed-in user can access — the set metrics are aggregated over.
    // NOTE: storeIds represents ALL stores the user has access to (network-wide) and is
    // intentionally independent of the currently selected store in Redux. Metrics are always
    // aggregated across the full network, not scoped to the single active store.
    const [storeIds, setStoreIds] = useState<string[]>([])
    const [bundle] = useBundle(BUNDLE_NAME)

    // Emit a load metric on mount, mirroring HomePage/IFrameController ToolLoaded metrics.
    useEffect(() => {
        const metricStoreId = getStoreId()
        new CountMetric(metricStoreId || undefined, 'ToolLoaded', 'JWOStoreManagementPlatform', 'OperationsPage').count(1)
    }, [])

    // Resolve the user's accessible stores from getUserStores (the authoritative list the caller is
    // authorized for). Only these stores are queried, so we never call the store-scoped APIs for a
    // store the user lacks access to. Uses `hidden` as a dep so a transient failure on first load
    // retries automatically when the user navigates back to Operations. Skips if already resolved.
    useEffect(() => {
        if (hidden || storeIds.length > 0) {
            return
        }
        let isMounted = true
        getUserAlias()
            .then((alias) => getUserStores(alias))
            .then((stores) => {
                if (isMounted) {
                    setStoreIds(stores.map((s) => s.storeId).filter((id): id is string => !!id))
                }
            })
            .catch((err) => {
                console.warn('SMP OperationsPage: failed to resolve accessible stores', err)
            })
        return () => {
            isMounted = false
        }
    }, [hidden])

    // Fetch each tab's metric lines across all accessible stores. Generic — any tab with a
    // fetchMetrics participates. Failures are non-fatal: that tab's card falls back to a "—".
    useEffect(() => {
        if (storeIds.length === 0) {
            setTabMetrics({})
            return
        }
        let isMounted = true
        tabs.forEach((tab) => {
            if (!tab.fetchMetrics) {
                return
            }
            setTabMetrics((prev) => ({ ...prev, [tab.id]: { loading: true, metrics: prev[tab.id]?.metrics } }))
            tab.fetchMetrics(storeIds, bundle)
                .then((metrics) => {
                    if (isMounted) {
                        setTabMetrics((prev) => ({ ...prev, [tab.id]: { loading: false, metrics } }))
                    }
                })
                .catch((err) => {
                    console.warn(`OperationsPage: failed to load metrics for tab "${tab.id}"`, err)
                    if (isMounted) {
                        setTabMetrics((prev) => ({ ...prev, [tab.id]: { loading: false } }))
                    }
                })
        })
        return () => {
            isMounted = false
        }
        // `bundle` is intentional: the metrics carry localized secondary text built
        // with t(), so a locale change has to re-derive them. It costs a re-fetch per
        // locale switch, which is rare and user-initiated.
    }, [storeIds, bundle])

    const onSelectTab = (tabId: string) => {
        setActiveTabId(tabId)
        setActivatedTabs((prev) => (prev.has(tabId) ? prev : new Set(prev).add(tabId)))
    }

    return (
        <div
            className="operations-page"
            data-atb-id="operations-page"
            style={hidden ? { display: 'none' } : undefined}
        >
            <div className="operations-page-header">
                <Header
                    variant="h1"
                    description={t(bundle, 'jsma-web-operations-description')}
                >
                    {t(bundle, 'jsma-web-operations-title')}
                </Header>
            </div>

            <div className="operations-tabs" data-atb-id="operations-tabs">
                <div className="operations-tab-strip" role="tablist" aria-label={t(bundle, 'jsma-web-operations-title')}>
                    {tabs.map((tab) => {
                        const isActive = tab.id === activeTabId
                        return (
                            <button
                                key={tab.id}
                                type="button"
                                role="tab"
                                id={`operations-tab-${tab.id}`}
                                aria-selected={isActive}
                                aria-controls={`operations-tabpanel-${tab.id}`}
                                className={`operations-tab ${isActive ? 'active' : ''}`}
                                data-atb-id={`operations-tab-${tab.id}`}
                                onClick={() => onSelectTab(tab.id)}
                            >
                                <Box
                                    fontSize="heading-s"
                                    fontWeight={isActive ? 'bold' : 'normal'}
                                    color="text-label"
                                >
                                    {t(bundle, tab.labelStringId)}
                                </Box>
                                {tab.fetchMetrics && renderMetricLines(tabMetrics[tab.id])}
                            </button>
                        )
                    })}
                </div>

                <div className="operations-tab-content">
                    {tabs.map((tab) => {
                        const isActive = tab.id === activeTabId
                        // keepMounted tabs: render once activated, hide via display when inactive
                        // (preserves iframes). Others: render only while active.
                        if (tab.keepMounted) {
                            if (!activatedTabs.has(tab.id)) {
                                return null
                            }
                            return (
                                <div
                                    key={tab.id}
                                    role="tabpanel"
                                    id={`operations-tabpanel-${tab.id}`}
                                    aria-labelledby={`operations-tab-${tab.id}`}
                                    className="operations-tab-panel"
                                    style={{ display: isActive ? 'block' : 'none' }}
                                >
                                    {tab.renderContent()}
                                </div>
                            )
                        }
                        return isActive
                            ? (
                                <div
                                    key={tab.id}
                                    role="tabpanel"
                                    id={`operations-tabpanel-${tab.id}`}
                                    aria-labelledby={`operations-tab-${tab.id}`}
                                    className="operations-tab-panel"
                                >
                                    {tab.renderContent()}
                                </div>
                            )
                            : null
                    })}
                </div>
            </div>
        </div>
    )
}

export default OperationsPage
